import dayjs from "dayjs";

// Pure builders: API payload in, ECharts option out. No React, no canvas, so
// every one of these is unit-testable and the page component stays thin.

export const PRACTICE_AREA_ORDER = [
  "VA Agency",
  "VA CAVC",
  "Personal Injury",
  "Workers Compensation",
  "SSDI",
  "Bankruptcy",
  "Mass Tort",
  "Other",
];

export const PRACTICE_AREA_COLORS = {
  "VA Agency": "#0C53BB",
  "VA CAVC": "#6A9BF5",
  "Personal Injury": "#E07A2F",
  "Workers Compensation": "#D9A520",
  SSDI: "#2F9E6B",
  Bankruptcy: "#8E5BD6",
  "Mass Tort": "#C94C6D",
  Other: "#98A6BA",
};

export const areaColor = (area) => PRACTICE_AREA_COLORS[area] || PRACTICE_AREA_COLORS.Other;

const FONT = "Manrope, sans-serif";
const AXIS_LABEL = { color: "#66758A", fontFamily: FONT, fontSize: 11 };
const SPLIT_LINE = { lineStyle: { color: "#EEF1F5" } };

export const monthLabel = (iso) => dayjs(iso).format("MMM YY");
export const dayLabel = (iso) => dayjs(iso).format("D MMM");

export const pct = (value, digits = 0) =>
  value === null || value === undefined ? "—" : `${(value * 100).toFixed(digits)}%`;

export const money = (value) => {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `$${Math.round(n).toLocaleString()}`;
};

export const signed = (value) => (value > 0 ? `+${value}` : `${value}`);

// Legends live ABOVE the plot, never below it: a bottom legend fights the
// month labels for the same strip of pixels and loses on narrow panels.
const baseGrid = { left: 44, right: 16, top: 28, bottom: 30, containLabel: false };
// Anchored to BOTH edges so a long legend wraps instead of clipping; the
// grid leaves room for two legend lines.
const gridWithLegend = { ...baseGrid, top: 56 };
const legendTop = { top: 0, left: 44, right: 16, textStyle: AXIS_LABEL, itemWidth: 10, itemHeight: 10, icon: "circle", itemGap: 14 };
const xAxisMonths = (months, extra = {}) => ({
  type: "category",
  data: months.map(monthLabel),
  axisLabel: { ...AXIS_LABEL, ...extra },
  axisTick: { show: false },
  axisLine: { lineStyle: { color: "#DDE2E9" } },
});

const tooltip = (extra = {}) => ({
  trigger: "axis",
  axisPointer: { type: "shadow" },
  backgroundColor: "#1B2028",
  borderWidth: 0,
  textStyle: { color: "#fff", fontFamily: FONT, fontSize: 12 },
  ...extra,
});

/** Months on the axis that fall entirely after the newest case data. */
export const monthsBeyondData = (months = [], dataThrough) => {
  if (!dataThrough) return [];
  const edge = dayjs(dataThrough).startOf("month");
  return months.filter((m) => dayjs(m).startOf("month").isAfter(edge));
};

/**
 * Shade the months the mirror has not reached yet and say so on the chart,
 * so an empty bar is never mistaken for "nobody signed up". (Staging runs
 * on a frozen snapshot; production refreshes nightly — either way the
 * chart tells the truth about what it knows.)
 */
export const noDataMarkArea = (months = [], dataThrough) => {
  const beyond = monthsBeyondData(months, dataThrough);
  if (!beyond.length) return undefined;
  return {
    silent: true,
    itemStyle: { color: "rgba(102,117,138,0.08)" },
    label: {
      show: true,
      position: "insideTop",
      color: "#66758A",
      fontFamily: FONT,
      fontSize: 11,
      lineHeight: 15,
      formatter: `No case data yet\n(as of ${dayjs(dataThrough).format("D MMM")})`,
    },
    data: [[{ xAxis: monthLabel(beyond[0]) }, { xAxis: monthLabel(beyond[beyond.length - 1]) }]],
  };
};

/**
 * One practice area's sign-ups by month, on its own scale. This is the
 * top-level chart of the dashboard (Matthew, 2026-10-15: "the top level
 * category is practice area"), so it gets real axes and a number on
 * every bar — it is not a sparkline.
 */
export const signupsAreaOption = (monthly = [], area, dataThrough) => {
  const months = monthly.map((m) => m.month);
  return {
    animationDuration: 300,
    grid: { ...baseGrid, top: 24 },
    tooltip: tooltip({ formatter: (items) => `<b>${items[0].name}</b><br/>${area}: <b>${items[0].value}</b>` }),
    xAxis: xAxisMonths(months),
    yAxis: { type: "value", axisLabel: AXIS_LABEL, splitLine: SPLIT_LINE, splitNumber: 4, minInterval: 1 },
    series: [
      {
        type: "bar",
        barMaxWidth: 36,
        itemStyle: { color: areaColor(area), borderRadius: [3, 3, 0, 0] },
        label: {
          show: true,
          position: "top",
          fontFamily: FONT,
          fontSize: 11,
          color: "#1B2028",
          // Zero bars carry no label; a tiny bar beside a tall one still
          // gets its number because at this width there is room for it.
          formatter: (p) => (p.value ? p.value.toLocaleString() : ""),
        },
        data: monthly.map((m) => ({ value: m.by_practice_area?.[area] || 0, month: m.month, area })),
        markArea: noDataMarkArea(months, dataThrough),
      },
    ],
  };
};

/** Conversion cohorts by month: stacked outcomes + the rate as a line. */
export const conversionMonthlyOption = (monthly = [], dataThrough) => {
  const months = monthly.map((m) => m.month);
  const stack = [
    { key: "converted", name: "Signed", color: "#2F9E6B" },
    { key: "in_progress", name: "Still working", color: "#D9A520" },
    { key: "turned_down", name: "Turned down", color: "#C94C6D" },
    { key: "referred_out", name: "Referred out", color: "#8E5BD6" },
    { key: "archived", name: "Archived", color: "#CBD2DC" },
  ];
  return {
    animationDuration: 400,
    grid: { ...gridWithLegend, right: 52 },
    tooltip: tooltip({
      formatter: (items) => {
        if (!items?.length) return "";
        const m = monthly[items[0].dataIndex] || {};
        const lines = items
          .filter((i) => i.seriesType === "bar" && i.value)
          .map((i) => `${i.marker} ${i.seriesName}: <b>${i.value}</b>`);
        lines.unshift(`<b>${monthLabel(m.month)}</b> — ${m.total || 0} intakes`);
        lines.push(`Conversion: <b>${pct(m.rate)}</b> · decided: <b>${pct(m.decided_rate)}</b>`);
        return lines.join("<br/>");
      },
    }),
    legend: legendTop,
    xAxis: xAxisMonths(months),
    yAxis: [
      { type: "value", axisLabel: AXIS_LABEL, splitLine: SPLIT_LINE, minInterval: 1 },
      { type: "value", min: 0, max: 1, axisLabel: { ...AXIS_LABEL, formatter: (v) => `${Math.round(v * 100)}%` }, splitLine: { show: false } },
    ],
    series: [
      ...stack.map((s, i) => ({
        name: s.name,
        type: "bar",
        stack: "cohort",
        barMaxWidth: 36,
        itemStyle: { color: s.color },
        emphasis: { focus: "series" },
        data: monthly.map((m) => ({ value: m[s.key] || 0, month: m.month, kind: s.key })),
        markArea: i === 0 ? noDataMarkArea(months, dataThrough) : undefined,
      })),
      {
        name: "Conversion rate",
        type: "line",
        yAxisIndex: 1,
        smooth: true,
        symbolSize: 6,
        lineStyle: { color: "#1B2028", width: 2 },
        itemStyle: { color: "#1B2028" },
        data: monthly.map((m) => (m.rate === null || m.rate === undefined ? null : Number(m.rate.toFixed(4)))),
      },
    ],
  };
};

/** Histogram of days from first contact to matter. */
export const timeHistogramOption = (histogram = [], color = "#0C53BB") => ({
  animationDuration: 400,
  grid: { ...baseGrid, bottom: 36 },
  tooltip: tooltip({ formatter: (items) => `${items[0].name}: <b>${items[0].value}</b> matters` }),
  xAxis: { type: "category", data: histogram.map((h) => h.label), axisLabel: { ...AXIS_LABEL, interval: 0, rotate: 0 }, axisTick: { show: false }, axisLine: { lineStyle: { color: "#DDE2E9" } } },
  yAxis: { type: "value", axisLabel: AXIS_LABEL, splitLine: SPLIT_LINE, minInterval: 1 },
  series: [
    {
      type: "bar",
      barMaxWidth: 44,
      itemStyle: { color, borderRadius: [4, 4, 0, 0] },
      data: histogram.map((h) => ({ value: h.count, from_days: h.from_days, to_days: h.to_days })),
    },
  ],
});

/** Monthly median + p90 of days-to-matter. */
export const timeTrendOption = (monthly = [], dataThrough) => ({
  animationDuration: 400,
  grid: gridWithLegend,
  tooltip: tooltip({
    axisPointer: { type: "line" },
    formatter: (items) => {
      const m = monthly[items[0].dataIndex] || {};
      return `<b>${monthLabel(m.month)}</b><br/>Median: <b>${m.median_days ?? "—"}</b> days<br/>90th percentile: <b>${m.p90_days ?? "—"}</b> days<br/>${m.count || 0} matters`;
    },
  }),
  legend: legendTop,
  xAxis: xAxisMonths(monthly.map((m) => m.month)),
  yAxis: { type: "value", axisLabel: { ...AXIS_LABEL, formatter: (v) => `${v}d` }, splitLine: SPLIT_LINE },
  series: [
    { name: "Median", type: "line", smooth: true, symbolSize: 6, lineStyle: { color: "#0C53BB", width: 2 }, itemStyle: { color: "#0C53BB" }, areaStyle: { color: "rgba(12,83,187,0.08)" }, data: monthly.map((m) => m.median_days), markArea: noDataMarkArea(monthly.map((m) => m.month), dataThrough) },
    { name: "90th percentile", type: "line", smooth: true, symbolSize: 4, lineStyle: { color: "#E07A2F", width: 1.5, type: "dashed" }, itemStyle: { color: "#E07A2F" }, data: monthly.map((m) => m.p90_days) },
  ],
});

/** Monthly costs advanced vs recovered for one practice area. */
export const costsMonthlyOption = (monthly = [], area = "VA Agency", dataThrough) => {
  const months = monthly.map((m) => m.month);
  const advColor = areaColor(area);
  return {
    animationDuration: 350,
    grid: { ...gridWithLegend, left: 56 },
    tooltip: tooltip({
      formatter: (items) => {
        if (!items?.length) return "";
        const m = monthly[items[0].dataIndex] || {};
        return [
          `<b>${monthLabel(m.month)}</b>`,
          `Advanced: <b>${money(m.advanced)}</b> (${m.advanced_count || 0} entries)`,
          `Recovered: <b>${money(m.recovered)}</b> (${m.recovered_count || 0} entries)`,
        ].join("<br/>");
      },
    }),
    legend: legendTop,
    xAxis: xAxisMonths(months),
    yAxis: {
      type: "value",
      axisLabel: {
        ...AXIS_LABEL,
        formatter: (v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`),
      },
      splitLine: SPLIT_LINE,
      splitNumber: 4,
    },
    series: [
      {
        name: "Advanced",
        type: "bar",
        barMaxWidth: 24,
        itemStyle: { color: advColor, borderRadius: [3, 3, 0, 0] },
        data: monthly.map((m) => ({ value: m.advanced || 0, month: m.month, kind: "costs_advanced" })),
        markArea: noDataMarkArea(months, dataThrough),
      },
      {
        name: "Recovered",
        type: "bar",
        barMaxWidth: 24,
        itemStyle: { color: "#2F9E6B", borderRadius: [3, 3, 0, 0] },
        data: monthly.map((m) => ({ value: m.recovered || 0, month: m.month, kind: "costs_recovered" })),
      },
    ],
  };
};

export const sparklineOption = (points = [], direction = "flat") => {
  const color = direction === "up" ? "#C94C6D" : direction === "down" ? "#2F9E6B" : "#98A6BA";
  return {
    animation: false,
    grid: { left: 0, right: 0, top: 2, bottom: 2 },
    xAxis: { type: "category", show: false, data: points.map((p) => p.day) },
    yAxis: { type: "value", show: false, scale: true },
    tooltip: { show: false },
    series: [{ type: "line", symbol: "none", smooth: true, lineStyle: { color, width: 1.5 }, areaStyle: { color, opacity: 0.12 }, data: points.map((p) => p.open) }],
  };
};

/**
 * Twelve months in a scorecard cell. The last point is the current,
 * incomplete month and is drawn hollow so it is not read as a drop.
 */
export const trendSparkOption = (values = [], months = [], color = "#0C53BB", format = (v) => v) => {
  const last = values.length - 1;
  return {
    animation: false,
    grid: { left: 2, right: 2, top: 4, bottom: 4 },
    xAxis: { type: "category", show: false, data: months.map(monthLabel) },
    yAxis: { type: "value", show: false, scale: true, min: (e) => Math.min(0, e.min) },
    tooltip: tooltip({ formatter: (items) => `<b>${items[0].name}</b>: ${items[0].value === null || items[0].value === undefined ? "—" : format(items[0].value)}` }),
    series: [
      {
        type: "line",
        symbol: "circle",
        symbolSize: (v, p) => (p.dataIndex === last ? 5 : 0),
        showSymbol: true,
        connectNulls: false,
        lineStyle: { color, width: 1.5 },
        areaStyle: { color, opacity: 0.1 },
        itemStyle: { color: "#fff", borderColor: color, borderWidth: 1.5 },
        data: values.map((v) => (v === undefined ? null : v)),
      },
    ],
  };
};

export const toCsv = (rows, columns) => {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.title)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(typeof c.value === "function" ? c.value(r) : r[c.key])).join(","));
  return [header, ...body].join("\n");
};

export const downloadText = (filename, text, type = "text/csv;charset=utf-8") => {
  if (typeof window === "undefined" || !window.URL?.createObjectURL) return;
  const blob = new Blob([text], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
